import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import Die2D from './Die2D';
import DiceBoard3D from './DiceBoard3D';
import ScoreCard from './ScoreCard';
import MaxiScoreCard from './MaxiScoreCard';
import HighScoreList from './HighScoreList';
import GameOverModal from './GameOverModal';
import AchievementNotification from './AchievementNotification';
import { getCompactMode } from '../utils/compactMode';
import AchievementsModal from './AchievementsModal';
import MobileMenu from './MobileMenu';
import StatisticsModal from './StatisticsModal';
import ParticleEffects from './ParticleEffects';
import GlowEffects from './GlowEffects';
import ChangelogModal, { CHANGELOG_EVENT } from './ChangelogModal';
import FunFactsModal from './FunFactsModal';
import { useVersionInfo } from '../context/VersionContext';
import { GameState, YatzyScores, Die as DieType } from '../types/game';
import { 
  toggleDieLock, 
  resetDice, 
  calculatePossibleScore, 
  calculateTotalScore,
  scoreCategories,
} from '../utils/gameLogic';
import { maxiScoreCategories, calculateMaxiScore, calculateMaxiTotals } from '../utils/maxiGameLogic';
import { mulberry32, todaySeed } from '../utils/rng';
import { soundManager } from '../utils/soundManager';
import { vibrateShort, vibrateMedium, vibrateScore, vibrateAchievement } from '../utils/haptics';
import { achievementManager, Achievement } from '../utils/achievementManager';
import { statisticsManager } from '../utils/statisticsManager';
import { themeManager } from '../utils/themeManager';
import { GameModeConfig } from './GameModeVariants';
import GameTimer from './GameTimer';
import { HighlightItem } from './HighlightsPanel';
import {
  markPlayedToday,
  shouldPromptForReminders,
  markReminderPromptShown,
  ensureNotificationPermission,
  scheduleDailyReminders,
} from '../utils/notifications';
import { trackEvent } from '../utils/umami';
import { saveGame, loadSavedGame, clearSavedGame, SavedGame } from '../utils/gameResume';
import { loadGhost, maybeSaveGhost, GhostGame } from '../utils/ghostMode';
import { useToast } from '../context/ToastContext';
import { useI18n } from '../i18n';
import StrategyHelper from './StrategyHelper';
import ChallengeIndicator from './ChallengeIndicator';
import {
  PositionedDie,
  SidebarSection,
  FabContainer,
  SpeedDialWrapper,
  DialActions,
  DialBtn,
} from './shared/GameStyles';

const standardForcedOrder = scoreCategories.map(category => category.key) as (keyof YatzyScores)[];
const reverseForcedOrder = [...standardForcedOrder].reverse() as (keyof YatzyScores)[];
// --- Series (Best of N) UI styled components ---
const SeriesPanel = styled.div`
  background: linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.04));
  border: 1px solid rgba(255,255,255,0.25);
  backdrop-filter: blur(12px) saturate(160%);
  border-radius: 18px;
  padding: 14px 18px 16px;
  color: #fff;
  width: min(760px, 94%);
  margin: 0 auto 26px;
  box-shadow: 0 6px 20px -4px rgba(0,0,0,0.45), 0 2px 6px rgba(0,0,0,0.35);
  position: relative;
  overflow: hidden;
  animation: fadeIn .5s ease;
  @keyframes fadeIn { from { opacity:0; transform: translateY(-6px);} to { opacity:1; transform: translateY(0);} }
`;
const SeriesHeader = styled.div`
  display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:10px;
  & h3 {margin:0;font-size:18px;letter-spacing:.5px;display:flex;align-items:center;gap:6px;}
`;
const SeriesList = styled.ul`
  list-style:none;margin:0 0 10px;padding:0;display:flex;flex-wrap:wrap;gap:10px;
`;
const SeriesItem = styled.li<{ $active?:boolean; $done?:boolean }>`
  background: ${p=>p.$active? 'linear-gradient(135deg,#4caf50,#2196f3)' : p.$done? 'linear-gradient(135deg,#2e7d32,#1565c0)' : 'rgba(255,255,255,0.08)'};
  color: ${p=>p.$active || p.$done ? '#fff' : 'rgba(255,255,255,0.85)'};
  padding:8px 12px;border-radius:12px;font-size:13px;font-weight:600;min-width:82px;text-align:center;position:relative;
  box-shadow:${p=>p.$active? '0 4px 14px -4px rgba(0,0,0,0.6)' : '0 2px 6px rgba(0,0,0,0.35)'};
  display:flex;flex-direction:column;gap:2px;
  &:after {content:'';position:absolute;inset:0;border-radius:inherit;border:1px solid rgba(255,255,255,0.18);pointer-events:none;}
`;
const SeriesStats = styled.div`
  display:flex;flex-wrap:wrap;gap:14px;font-size:13px;margin-top:2px;opacity:.95;
  & span {display:flex;align-items:center;gap:4px;}
`;
const SeriesProgressOuter = styled.div`
  position:relative;height:8px;border-radius:5px;background:rgba(255,255,255,0.15);overflow:hidden;margin-top:6px;box-shadow:inset 0 1px 3px rgba(0,0,0,0.4);
`;
const SeriesProgressInner = styled.div<{ $pct:number }>`
  position:absolute;left:0;top:0;bottom:0;width:${p=>p.$pct}%;background:linear-gradient(90deg,#43a047,#1e88e5);box-shadow:0 0 0 1px rgba(255,255,255,0.2),0 2px 6px -2px rgba(0,0,0,0.6);transition:width .5s cubic-bezier(.25,.7,.3,1);
`;
const SeriesToggleBtn = styled.button`
  background:rgba(255,255,255,0.12);color:#fff;border:1px solid rgba(255,255,255,0.25);border-radius:10px;padding:6px 12px;font-size:12px;font-weight:600;cursor:pointer;display:flex;align-items:center;gap:4px;backdrop-filter:blur(6px) saturate(140%);transition:all .25s;
  &:hover {background:rgba(255,255,255,0.2);} &:active {transform:scale(.96);} 
`;
// CHANGE (2026-02-23): "Nollställ"-knapp borttagen enligt krav (inga rensa/nollställ-knappar).

// Minimal inline modal for series summary (kept internal to avoid new file overhead)
const SeriesModalBackdrop = styled.div`
  position:fixed;inset:0;background:rgba(0,0,0,0.55);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;z-index:2500;padding:20px;
`;
const SeriesModalCard = styled.div`
  background:linear-gradient(145deg,#1e3c26,#13401f);color:#fff;border:2px solid rgba(255,255,255,.18);border-radius:18px;box-shadow:0 10px 32px -8px rgba(0,0,0,.6),0 4px 12px rgba(0,0,0,.4);width:min(520px,92%);padding:26px 26px 24px;position:relative;overflow:hidden;
`;
const SeriesRoundsList = styled.ul`
  list-style:none;margin:12px 0 18px;padding:0;display:flex;flex-direction:column;gap:8px;max-height:220px;overflow:auto;
  & li {background:rgba(255,255,255,0.08);padding:10px 14px;border-radius:12px;display:flex;justify-content:space-between;align-items:center;font-size:14px;position:relative;}
  & li.best {background:linear-gradient(135deg,#2e7d32,#1565c0);} 
`;
const CloseSeriesBtn = styled.button`
  position:absolute;top:10px;right:12px;background:rgba(255,255,255,0.15);border:none;color:#fff;font-size:20px;width:40px;height:40px;border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(4px);transition:all .25s;
  &:hover {background:rgba(255,255,255,0.25);} &:active{transform:scale(.92);}
`;
const SeriesPrimaryBtn = styled.button`
  background:linear-gradient(135deg,#388e3c,#1976d2);color:#fff;border:none;border-radius:12px;padding:12px 20px;font-size:15px;font-weight:600;cursor:pointer;box-shadow:0 6px 18px -6px rgba(0,0,0,.6),0 2px 6px rgba(0,0,0,.4);display:flex;align-items:center;gap:8px;transition:all .25s;
  &:hover {filter:brightness(1.1);} &:active {transform:translateY(2px);}
`;
const SeriesSecondaryBtn = styled.button`
  background:rgba(255,255,255,0.12);color:#fff;border:1px solid rgba(255,255,255,0.25);border-radius:12px;padding:12px 18px;font-size:14px;font-weight:600;cursor:pointer;display:flex;align-items:center;gap:8px;transition:all .25s;
  &:hover {background:rgba(255,255,255,0.2);} &:active {transform:translateY(1px);}
`;

const GameContainer = styled.div`
  min-height: 100vh;
  /* Always reflect current theme variable; avoid hard fallback reset */
  background: var(--theme-background);
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: background 0.5s ease;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
  color: white;
`;

const toastIn = keyframes`0%{transform:translateY(-6px);opacity:0;}100%{transform:translateY(0);opacity:1;}`;
const toastOut = keyframes`0%{transform:translateY(0);opacity:1;}100%{transform:translateY(-4px);opacity:0;}`;
const ToastStack = styled.div`position:fixed;top:calc(env(safe-area-inset-top,0) + 10px);left:50%;transform:translateX(-50%);z-index:1600;display:flex;flex-direction:column;gap:8px;width:min(94%,480px);pointer-events:none;`;
const ToastMsg = styled.div<{ $leaving?:boolean }>`background:linear-gradient(135deg,#1b5e20,#2e7d32);color:#fff;font-weight:600;font-size:14px;padding:12px 16px;border-radius:12px;box-shadow:0 8px 20px -6px rgba(0,0,0,.5),0 2px 6px rgba(0,0,0,.4);border:1px solid rgba(255,255,255,0.18);backdrop-filter:blur(14px) saturate(160%);animation:${toastIn} .45s ease, ${p=>p.$leaving?toastOut:toastIn} .4s ease ${p=>p.$leaving?'.45s':''} forwards;`;
const Title = styled.h1`
  font-size: 48px;
  margin: 0 0 10px 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 1024px) {
    font-size: 40px;
  }
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  font-size: 18px;
  margin: 0;
  opacity: 0.9;
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// Ghost mode: diskret jämförelserad mot rekordspelet. Pil + text bär
// informationen (inte enbart färg).
const GhostCompareRow = styled.div<{ $ahead: boolean }>`
  margin-top: 6px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${p => (p.$ahead ? '#2e7d32' : '#c62828')};
  background: ${p => (p.$ahead ? 'rgba(76,175,80,0.12)' : 'rgba(244,67,54,0.10)')};
  border-radius: 8px;
  padding: 4px 10px;
`;

const GameBoard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh; /* Säkerställ att vi har full höjd */

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    gap: 32px;
    min-height: calc(100vh - 140px); /* Minus header och marginal */
  }
  
  /* Stora skärmar - större layout */
  @media (min-width: 1400px) {
    max-width: 1600px;
    gap: 40px;
  }
  
  @media (min-width: 1920px) {
    max-width: 2000px;
    gap: 50px;
  }
  
  @media (orientation: landscape) and (max-height: 520px) {
    align-items: stretch;
    gap: 24px;
  }
  
  @media (max-width: 768px) {
    padding-bottom: calc(80px + env(safe-area-inset-bottom)); /* room for sticky action bar */
  }
`;

const DiceSection = styled.div`
  background: var(--theme-card-background, white);
  border: 2px solid var(--theme-card-border, #ddd);
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 320px;
  transition: all 0.5s ease;
  width: 100%;
  max-width: 100%;

  @media (min-width: 768px) {
    flex: 1 1 0;
    /* CHANGE (2026-08-15): Bredare spelplan – 5–6 tärningar behöver plats för
       att kunna läsas av (samma mått som lokal multiplayer). */
    max-width: 620px;
  }
  
  /* Sticky tärningar i landskapsläge på telefoner */
  @media (max-height: 600px) and (min-width: 600px) {
    position: sticky;
    top: 20px;
    z-index: 10;
    margin-bottom: 20px;
  }
  
  /* 1400px+ skärmar - större tärningssektion */
  @media (min-width: 1400px) {
    max-width: 800px;
    padding: 40px;
    border-radius: 20px;
    border: 3px solid var(--theme-card-border, #ddd);
  }
  
  /* 1920px+ skärmar - mycket större för 4K */
  @media (min-width: 1920px) {
    max-width: 1100px;
    padding: 50px;
    border-radius: 24px;
    border: 4px solid var(--theme-card-border, #ddd);
  }
  
  /* 2560px+ skärmar - maximal storlek för ultrawide/4K+ */
  @media (min-width: 2560px) {
    max-width: 1400px;
    padding: 60px;
    border-radius: 28px;
    border: 5px solid var(--theme-card-border, #ddd);
  }
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
  }
  
  @media (orientation: landscape) and (max-height: 520px) {
    padding: 20px;
    min-width: 280px;
    max-width: 360px;
    border-radius: 14px;
  }
`;

const DiceContainer = styled.div<{ $tall?: boolean }>`
  /* Felt-style background to match scorecard (theme adaptive) */
  position: relative;
  width: 100%;
  max-width: 600px;
  /* CHANGE (2026-08-15): Högre plan så tärningarna kan läggas på flera rader
     utan överlapp (samma mått som lokal multiplayer). */
  height: ${p => p.$tall ? '460px' : '300px'};
  margin: 0 auto 18px;
  border-radius: 20px;
  background: linear-gradient(140deg, var(--felt-dark-color,#094921) 0%, var(--felt-base-color,#0f6d2f) 55%, var(--felt-light-color,#158a3c) 100%);
  box-shadow:
    inset 0 1px 3px rgba(255,255,255,0.15),
    inset 0 -1px 4px rgba(0,0,0,0.25),
    0 6px 18px -4px rgba(0,0,0,0.35),
    0 2px 6px rgba(0,0,0,0.18);
  border: 2px solid var(--felt-edge-color,#062d15);
  outline: 1px solid rgba(255,255,255,0.06);
  overflow: hidden;
  backdrop-filter: blur(6px) saturate(160%);
  padding: 4px;
  display: block;
  &::before, &::after { content:''; position:absolute; inset:0; pointer-events:none; }
  &::before { /* subtle fiber/noise */
    background: repeating-linear-gradient(45deg, rgba(255,255,255,0.02) 0 2px, transparent 2px 4px);
    mix-blend-mode: overlay; opacity:.15;
  }
  &::after { /* subtle vignette */
    background: radial-gradient(circle at center, rgba(0,0,0,0) 70%, rgba(0,0,0,0.15) 100%);
    opacity: .3;
  }
  
  /* 1400px+ skärmar - större tärningsområde */
  @media (min-width: 1400px) {
    max-width: 800px;
    height: 380px;
    border-radius: 24px;
    border: 3px solid var(--felt-edge-color,#062d15);
    padding: 6px;
  }
  
  /* 1920px+ skärmar - mycket större tärningsområde för 4K */
  @media (min-width: 1920px) {
    max-width: 1100px;
    height: 500px;
    border-radius: 28px;
    border: 4px solid var(--felt-edge-color,#062d15);
    padding: 8px;
  }
  
  /* 2560px+ skärmar - maximal tärningsområde för ultrawide/4K+ */
  @media (min-width: 2560px) {
    max-width: 1400px;
    height: 600px;
    border-radius: 32px;
    border: 5px solid var(--felt-edge-color,#062d15);
    padding: 10px;
  }
  
  @media (max-width: 640px) { height: ${p => p.$tall ? '380px' : '260px'}; }
  @media (max-width: 520px) {
    /* Behåller spelplansbakgrunden även på smala skärmar */
    height: auto; 
    min-height: 100px; 
    display: flex; 
    flex-wrap: nowrap; 
    overflow-x: auto; 
    gap: 12px; 
    padding: 12px 8px; 
    align-items: center; 
    justify-content: center;
    /* Säkerställer att background syns även på smala skärmar */
    background: var(--theme-dice-area-background, 
      linear-gradient(140deg, var(--felt-dark-color,#094921) 0%, var(--felt-base-color,#0f6d2f) 55%, var(--felt-light-color,#158a3c) 100%)
    ) !important;
    & > div { position: static !important; transform: none !important; }
  }
  
  @media (orientation: landscape) and (max-height: 520px) {
    height: 220px;
    max-width: 420px;
    border-radius: 18px;
  }
`;

// Mobile dice container - två rader för bättre plats
const MobileDiceContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 18px;
  margin: 0 auto 18px;
  padding: 22px;
  max-width: clamp(320px, 88vw, 380px);
  min-height: clamp(200px, 52vw, 260px);
  background: linear-gradient(140deg, var(--felt-dark-color,#094921) 0%, var(--felt-base-color,#0f6d2f) 55%, var(--felt-light-color,#158a3c) 100%);
  border-radius: 20px;
  border: 2px solid var(--felt-edge-color,#062d15);
  box-shadow:
    inset 0 1px 3px rgba(255,255,255,0.15),
    inset 0 -1px 4px rgba(0,0,0,0.25),
    0 6px 18px -4px rgba(0,0,0,0.35);
  justify-items: center;
  align-items: center;
  overflow: hidden;
  
  /* För 6 tärningar - 3x2 grid */
  &.six-dice {
    grid-template-columns: repeat(3, 1fr);
    max-width: clamp(340px, 92vw, 420px);
  }
  
  /* För 5 tärningar - 3 på första raden, 2 på andra */
  &.five-dice {
    grid-template-columns: repeat(3, 1fr);
    max-width: clamp(320px, 88vw, 380px);
    
    & > div:nth-child(4) {
      grid-column: 1 / 2;
    }
    & > div:nth-child(5) {
      grid-column: 3 / 4;
    }
  }
  
  @media (max-width: 480px) {
    max-width: clamp(290px, 92vw, 340px);
    gap: 14px;
    padding: 18px;
    min-height: clamp(180px, 52vw, 230px);
  }
  
  /* Landscape orientation - very compact layout */
  @media (orientation: landscape) and (max-height: 600px) {
    gap: 12px;
    padding: 16px;
    max-width: min(90vw, 100%);
    min-height: auto;
    margin-bottom: 12px;
  }
  
  @media (orientation: landscape) and (max-height: 450px) {
    gap: 8px;
    padding: 10px;
    min-height: auto;
  }
  
  @media (orientation: landscape) and (max-height: 520px) {
    max-width: clamp(360px, 70vw, 460px);
    min-height: clamp(180px, 60vh, 240px);
    gap: 16px;
    padding: 20px;
  }
`;

const GameInfo = styled.div`
  text-align: center;
  margin-bottom: 20px;
  color: var(--theme-text, #333);
  transition: color 0.5s ease;
`;

const RollInfo = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
  color: var(--theme-secondary, #2196F3);
  transition: color 0.5s ease;
`;

const RoundInfo = styled.div`
  font-size: 16px;
  color: #666;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 768px) {
    position: sticky;
    bottom: 0;
    background: var(--theme-card-background, rgba(255,255,255,0.95));
    padding: 10px 12px;
    backdrop-filter: saturate(180%) blur(6px);
    z-index: 50;
    left: 0;
    right: 0;
    gap: 10px;
  padding-bottom: calc(10px + env(safe-area-inset-bottom));
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  background: ${props => {
    if (props.$variant === 'danger') return '#f44336';
    if (props.$variant === 'secondary') return '#757575';
    return '#4CAF50';
  }};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: ${props => {
      if (props.$variant === 'danger') return '#d32f2f';
      if (props.$variant === 'secondary') return '#616161';
      return '#45a049';
    }};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media (max-width: 768px) {
    flex: 1 1 auto;
    min-width: 120px;
  }
  
  @media (max-width: 480px) {
    padding: 14px 24px;
    font-size: 17px;
    border-radius: 10px;
    min-width: 130px;
  }
`;

// Instructions component removed (info accessible via help modal)

const Fab = styled.button<{ $pos?: 'br' | 'bl' | 'tr' | 'tl' }>`
  position: fixed;
  z-index: 1100;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  color: #fff;
  background: linear-gradient(135deg, color-mix(in srgb, var(--theme-secondary,#2196f3) 85%, #000), color-mix(in srgb, var(--theme-primary,#4caf50) 85%, #000));
  outline: 2px solid rgba(255,255,255,0.15);
  outline-offset: -2px;
  box-shadow: 0 6px 16px rgba(0,0,0,0.3);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.2s;
  
  /* Standardiserade positioner med safe area */
  ${(p) => p.$pos === 'br' && `
    bottom: calc(20px + env(safe-area-inset-bottom, 0));
    right: calc(20px + env(safe-area-inset-right, 0));
  `}
  ${(p) => p.$pos === 'bl' && `
    bottom: calc(20px + env(safe-area-inset-bottom, 0));
    left: calc(20px + env(safe-area-inset-left, 0));
  `}
  ${(p) => p.$pos === 'tr' && `
    top: calc(20px + env(safe-area-inset-top, 0));
    right: calc(20px + env(safe-area-inset-right, 0));
  `}
  ${(p) => p.$pos === 'tl' && `
    top: calc(20px + env(safe-area-inset-top, 0));
    left: calc(20px + env(safe-area-inset-left, 0));
  `}
  
  &:active { transform: scale(0.9); }
  &:hover { transform: scale(1.05); }
  
  @media (max-width: 640px) {
    width: 52px;
    height: 52px;
    font-size: 24px;
  }
`;

// Modern hemknapp styling
const HomeButton = styled(Fab)`
  background: linear-gradient(135deg, 
    color-mix(in srgb, var(--theme-primary,#4caf50) 80%, #fff), 
    color-mix(in srgb, var(--theme-secondary,#2196f3) 80%, #fff)
  );
  box-shadow: 0 8px 24px rgba(0,0,0,0.25), 0 4px 8px rgba(76,175,80,0.3);
  border: 3px solid rgba(255,255,255,0.3);
  
  &:hover {
    box-shadow: 0 12px 32px rgba(0,0,0,0.3), 0 6px 12px rgba(76,175,80,0.4);
    transform: scale(1.08);
  }
`;

const MainDial = styled(Fab)`
  width: 64px;
  height: 64px;
  font-size: 30px;
  will-change: transform;
  transform: translateZ(0);
  position: relative;
  
  &:hover {
    transform: translateZ(0) scale(1.05);
  }
  
  &:active {
    transform: translateZ(0) scale(0.95);
  }
  
  @media (max-width: 640px) {
    width: 56px;
    height: 56px;
    font-size: 26px;
  }
`;

interface YatzyGameProps {
  mode?: GameModeConfig;
  /** Återuppta sparat spel (yatzy-active-game) istället för att starta nytt. */
  resume?: boolean;
  /** Navigera hem utan sidomladdning. Faller tillbaka på location.href om den saknas. */
  onExit?: () => void;
}

const YatzyGame: React.FC<YatzyGameProps> = ({ mode, resume, onExit }) => {
  const { showConfirmationToast, showToast } = useToast();
  const { t, locale } = useI18n();
  const diceCount = mode?.diceCount ?? 5;
  const maxRolls = mode?.maxRolls ?? 3;
  const timeLimit = mode?.timeLimit;
  const modeId = mode?.id || (diceCount === 6 ? 'maxi' : 'classic');
  const isMaxi = diceCount === 6;
  const isDaily = modeId === 'daily';
  const seriesLength = mode?.seriesLength ?? 1;
  // CHANGE (2026-02-23): Blitz ska inte ge dubbla poäng.
  const scoreMultiplier = 1;
  // CHANGE (2026-08-15): Pågående spel sparas löpande så att det kan återupptas
  // från startskärmen. Daily (seedad RNG), serier (egen persistens) och
  // challenge (progress utanför gameState) undantas.
  const canPersistGame = !isDaily && seriesLength === 1 && modeId !== 'challenge';
  // Läs ev. sparat spel EN gång vid mount (inte vid varje render).
  const resumedGameRef = useRef<SavedGame | null | undefined>(undefined);
  if (resumedGameRef.current === undefined) {
    const saved = resume ? loadSavedGame() : null;
    resumedGameRef.current = saved && saved.modeId === modeId ? saved : null;
  }

  // Get roll button text based on rolls left
  const getRollButtonText = (rollsLeft: number): string => {
    if (rollsLeft === maxRolls) {
      return t('game.rollBtn.new');
    }
    if (rollsLeft === 0) {
      return t('game.rollBtn.none');
    }
    const castNr = maxRolls - rollsLeft + 1;
    const remainingAfter = rollsLeft - 1;
    if (remainingAfter === 0) {
      return t('game.rollBtn.last');
    }
    return t('game.rollBtn.mid', { n: castNr, left: remainingAfter });
  };

  // Game state
  const [gameState, setGameState] = useState<GameState>(() => {
    // CHANGE (2026-08-15): Återuppta sparat spel om ett giltigt sådant finns.
    if (resumedGameRef.current) {
      return { ...resumedGameRef.current.gameState, isGameComplete: false };
    }
    const classic: YatzyScores = {
      ones: null, twos: null, threes: null, fours: null, fives: null, sixes: null,
      onePair: null, twoPairs: null, threeOfAKind: null, fourOfAKind: null,
      smallStraight: null, largeStraight: null, fullHouse: null, chance: null, yatzy: null 
    };
    const maxi: any = { 
      ones: null, twos: null, threes: null, fours: null, fives: null, sixes: null,
      onePair: null, twoPairs: null, threeOfAKind: null, fourOfAKind: null, fiveOfAKind: null,
      smallStraight: null, largeStraight: null, fullStraight: null, hus: null, torn: null,
      chance: null, yatzy: null 
    };
    
    return {
      dice: resetDice(diceCount as 5 | 6) as any,
      rollsLeft: maxRolls,
      scores: (diceCount === 6 ? maxi : classic) as any,
      currentRound: 1,
      isGameComplete: false,
      totalScore: 0,
      upperSectionScore: 0,
      upperSectionBonus: 0,
    };
  });

  // Visual states
  const [isRolling, setIsRolling] = useState(false);
  const [showHighScores, setShowHighScores] = useState(false);
  const [showGameOver, setShowGameOver] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showAchievements, setShowAchievements] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showFunFacts, setShowFunFacts] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialOpen, setDialOpen] = useState(false);
  const [justScoredCategory, setJustScoredCategory] = useState<string | null>(null);
  const [firstRunChecked, setFirstRunChecked] = useState(false);
  
  // Dice layout states
  const [dicePositions, setDicePositions] = useState<{x: number; y: number;}[]>([]);
  const [isNarrow, setIsNarrow] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const w = window.innerWidth;
      const h = window.innerHeight;
      // Detect mobile: pointer is coarse (touch device) or small screen
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                       (typeof window !== 'undefined' && window.matchMedia('(pointer:coarse)').matches) ||
                       (w < 1024 && h < 1024); // Small screens
      // Mobile always uses 2D (narrow), desktop uses 3D unless very narrow
      return isMobile || w < 640 || (w < 900 && h < 500);
    }
    return true;
  });
  // Effect states
  const [particleEffect, setParticleEffect] = useState<{
    type: 'yatzy' | 'record' | 'achievement' | 'glow' | null;
    x?: number;
    y?: number;
    intensity?: number;
  }>({ type: null });
  const [diceGlowType, setDiceGlowType] = useState<
    'yatzy' | 'straight' | 'fullhouse' | 'fourOfAKind' | 'threeOfAKind' | null
  >(null);
  
  // Game tracking states
  const [gameStartTime, setGameStartTime] = useState<number>(() => resumedGameRef.current?.gameStartTime ?? Date.now());
  const [rollsUsedPerCategory, setRollsUsedPerCategory] = useState<number[]>(() => resumedGameRef.current?.rollsUsedPerCategory ?? []);
  const [timerActive, setTimerActive] = useState(false);
  const [dailyCountdown, setDailyCountdown] = useState<string | null>(null);
  const [currentAchievement, setCurrentAchievement] = useState<Achievement | null>(null);
  const [highlights, setHighlights] = useState<HighlightItem[]>([]);
  
  // Challenge mode state
  const [challengeGoal, setChallengeGoal] = useState<string>('');
  const [challengeType, setChallengeType] = useState<string>('');
  const [challengeTarget, setChallengeTarget] = useState<number>(0);
  const [challengeProgress, setChallengeProgress] = useState<number>(0);
  const [challengeCompleted, setChallengeCompleted] = useState<boolean>(false);
  const [challengePoints, setChallengePoints] = useState<number>(0);
  const [aiAssistantEnabled, setAiAssistantEnabled] = useState(() => {
    try {
      return localStorage.getItem('yatzy-ai-assistant-enabled') === 'true';
    } catch {
      return false;
    }
  });
  const [effectsEnabled, setEffectsEnabled] = useState(() => {
    try { return localStorage.getItem('yatzy-effects-enabled') !== 'false'; } catch { return true; }
  });
  const [shakeToRollEnabled, setShakeToRollEnabled] = useState(() => {
    try { return localStorage.getItem('yatzy-shake-to-roll') === 'true'; } catch { return false; }
  });
  // Ångra senaste kategorival: snapshot av läget precis före commitScore.
  // Giltig tills nästa kast/val. Erbjuds inte i tidslägen (timer-krock),
  // challenge (progress ligger utanför gameState) eller för sista kategorin.
  const lastMoveRef = useRef<{ category: string; dice: DieType[]; rollsLeft: number; currentRound: number } | null>(null);
  // CHANGE (2026-08-15): Ghost mode – kumulativ total per spelad omgång,
  // jämförs mot rekordspelets utveckling (ghostMode.ts). Återställs vid resume.
  const roundTotalsRef = useRef<number[]>(resumedGameRef.current?.roundTotals ?? []);
  const [ghost] = useState<GhostGame | null>(() => (modeId !== 'challenge' ? loadGhost(modeId) : null));
  // Long-press timers per die id
  const longPressTimers = useRef<Record<number, number>>({});
  const handleRollDiceRef = useRef<() => void | Promise<void>>(() => {});
  const handleDieClickRef = useRef<(dieId: number) => void>(() => {});
  const resetGameStateRef = useRef<() => void>(() => {});

  // Toast handling
  const [toast, setToast] = useState<{ id: number; msg: string; leaving?: boolean } | null>(null);
  const toastCounter = useRef(0);
  const toastTimeouts = useRef<{ leave?: NodeJS.Timeout; remove?: NodeJS.Timeout }>({});
  const seriesScoresRef = useRef<number[]>([]);
  const [seriesIdx, setSeriesIdx] = useState<number>(0);
  const [seriesScores, setSeriesScores] = useState<number[]>([]);
  const [seriesPanelOpen, setSeriesPanelOpen] = useState<boolean>(() => {
    try { return localStorage.getItem('yatzy-series-panel-open') !== 'false'; } catch { return true; }
  });
  const SERIES_STORE_KEY = React.useMemo(()=> `series-progress:${modeId}:${seriesLength}`, [modeId, seriesLength]);
  const SERIES_GAME_STATE_KEY = React.useMemo(()=> `series-current:${modeId}:${seriesLength}`, [modeId, seriesLength]);
  const gameCountedRef = useRef<boolean>(false);
  const [seriesSummaryOpen, setSeriesSummaryOpen] = useState(false);
  const [seriesCompletedData, setSeriesCompletedData] = useState<{ scores: number[]; best: number; avg: number;} | null>(null);
  
  // Other refs
  const scoreCardRef = useRef<HTMLDivElement | null>(null);
  const diceAreaRef = useRef<HTMLDivElement | null>(null);
  const rngRef = useRef<(() => number) | null>(null);
  const { info: versionInfo } = useVersionInfo();

  // Category name mapping (klassiska + maxi-specifika kategorier)
  const categoryNameMap = useMemo(() => {
    const m = new Map<string, string>();
    scoreCategories.forEach(c => m.set(c.key as string, c.name));
    maxiScoreCategories.forEach(c => { if (!m.has(c.key as string)) m.set(c.key as string, c.name); });
    return m;
  }, []);
  
  const getCategoryName = useCallback((key: string) => {
    const i18nKey = `cat.${key}.name`;
    const translated = t(i18nKey);
    return translated !== i18nKey ? translated : (categoryNameMap.get(key) || key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryNameMap, locale]);

  const dailyDateKey = React.useMemo(() => {
    if (!isDaily) return modeId;
    const d = new Date();
    const key = `${d.getUTCFullYear()}-${(d.getUTCMonth()+1).toString().padStart(2,'0')}-${d.getUTCDate().toString().padStart(2,'0')}`;
    return `daily:${key}`;
  }, [isDaily, modeId]);
  
  // Effect: Daily countdown (separate clean effect)
  useEffect(() => {
    if (!isDaily) { setDailyCountdown(null); return; }
    const update = () => {
      const now = new Date();
      const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0));
      const ms = next.getTime() - now.getTime();
      const totalSec = Math.max(0, Math.floor(ms / 1000));
      const h = Math.floor(totalSec / 3600).toString().padStart(2, '0');
      const m = Math.floor((totalSec % 3600) / 60).toString().padStart(2, '0');
      const s = Math.floor(totalSec % 60).toString().padStart(2, '0');
      setDailyCountdown(`${h}:${m}:${s}`);
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [isDaily]);

  // CHANGE (2026-08-15): Spara pågående spel löpande för "Fortsätt spel" på
  // startskärmen. Rensas när spelet är klart (eller vid nytt spel/reset).
  useEffect(() => {
    if (!canPersistGame) return;
    if (gameState.isGameComplete) { clearSavedGame(); return; }
    const started = Object.values(gameState.scores).some(v => v !== null) || gameState.rollsLeft < maxRolls;
    if (!started) return;
    saveGame({ modeId, gameState, gameStartTime, rollsUsedPerCategory, savedAt: Date.now(), roundTotals: roundTotalsRef.current });
  }, [gameState, canPersistGame, modeId, gameStartTime, rollsUsedPerCategory, maxRolls]);

  // Kom ihåg senaste singleplayer-läget ("Spela"-knappen på startskärmen).
  useEffect(() => {
    try { localStorage.setItem('yatzy-last-single-mode', modeId); } catch {}
  }, [modeId]);

  // Listen for explicit open-full-changelog events
  useEffect(() => {
    const openFull = () => setChangelogOpen(true);
    window.addEventListener(CHANGELOG_EVENT, openFull);
    return () => window.removeEventListener(CHANGELOG_EVENT, openFull);
  }, []);

  // Auto-open changelog if version changed since last visit
  useEffect(() => {
    if (firstRunChecked) return; // guard
    if (!versionInfo?.version) return; // wait for version info
    try {
      const lastSeen = localStorage.getItem('lastSeenVersion');
      if (lastSeen && lastSeen !== versionInfo.version) {
        // only auto-open for actual updates (not first ever visit)
        setChangelogOpen(true);
      }
      localStorage.setItem('lastSeenVersion', versionInfo.version);
    } catch {}
    setFirstRunChecked(true);
  }, [versionInfo?.version, firstRunChecked]);

  // Resume audio context on first interaction
  useEffect(() => {
    const handleFirstInteraction = () => {
      soundManager.resumeAudioContext();
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };

    document.addEventListener('click', handleFirstInteraction);
    document.addEventListener('keydown', handleFirstInteraction);

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };
  }, []);

  // Helpers: long-press to toggle die lock (mobile-friendly)
  const startLongPress = useCallback((dieId: number) => {
    // Only allow lock/unlock after first roll, same as click rule
    if (gameState.rollsLeft >= maxRolls) return;
    // Avoid stacking timers
    if (longPressTimers.current[dieId]) clearTimeout(longPressTimers.current[dieId]);
    longPressTimers.current[dieId] = window.setTimeout(() => {
      handleDieClickRef.current(dieId);
      delete longPressTimers.current[dieId];
    }, 420);
  }, [gameState.rollsLeft, maxRolls]);

  const cancelLongPress = useCallback((dieId: number) => {
    const t = longPressTimers.current[dieId];
    if (t) {
      clearTimeout(t);
      delete longPressTimers.current[dieId];
    }
  }, []);

  // Global keyboard shortcuts för smidigare spel
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (e.repeat) return;

      const key = e.key;
      if (key === 'r' || key === 'R') {
        if (!isRolling && gameState.rollsLeft > 0) {
          e.preventDefault();
          handleRollDiceRef.current?.();
        }
      } else if (key >= '1' && key <= '6') {
        const idx = parseInt(key, 10) - 1;
        if (idx < gameState.dice.length && gameState.rollsLeft < maxRolls) {
          const dieId = gameState.dice[idx]?.id;
          if (dieId) {
            e.preventDefault();
            handleDieClickRef.current(dieId);
          }
        }
      } else if (key === 'h' || key === '?') {
        e.preventDefault();
        setShowHelp(v => !v);
      } else if (key === 's' || key === 'S') {
        e.preventDefault();
        setShowStatistics(true);
      } else if (key === 't' || key === 'T') {
        e.preventDefault();
        setShowHighScores(true);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [gameState.dice, gameState.rollsLeft, isRolling, maxRolls]);

  // Initialize theme / achievement namespace when mode or daily flag changes
  // Generate random challenge for Challenge Mode
  const generateChallenge = useCallback(() => {
    if (modeId !== 'challenge') return;
    
    const challenges = [
      { type: 'upperSection', goal: 'Få 20+ poäng i övre sektionen', target: 20, points: 20 },
      { type: 'fullHouse', goal: 'Få kåk med värde 25+', target: 25, points: 25 },
      { type: 'yatzy', goal: 'Få yatzy', target: 50, points: 50 },
      { type: 'threes', goal: 'Få 12+ poäng med treor', target: 12, points: 15 },
      { type: 'fours', goal: 'Få 16+ poäng med fyror', target: 16, points: 15 },
      { type: 'sixes', goal: 'Få 24+ poäng med sexor', target: 24, points: 30 },
      { type: 'pair', goal: 'Få ett par med 10+', target: 10, points: 15 },
      { type: 'twoPairs', goal: 'Få två par med 20+', target: 20, points: 20 },
      { type: 'threeOfAKind', goal: 'Få triss med värde 15+', target: 15, points: 20 },
      { type: 'fourOfAKind', goal: 'Få fyrtal med värde 20+', target: 20, points: 30 },
      { type: 'smallStraight', goal: 'Få liten stege', target: 15, points: 25 },
      { type: 'largeStraight', goal: 'Få stor stege', target: 20, points: 30 },
      { type: 'fewRolls', goal: 'Använd max 1 kast för en kategori', target: 1, points: 20 },
      { type: 'bonus', goal: 'Uppnå bonus i övre sektionen', target: 63, points: 35 }
    ];
    
    const selected = challenges[Math.floor(Math.random() * challenges.length)];
    
    setChallengeType(selected.type);
    setChallengeGoal(selected.goal);
    setChallengeTarget(selected.target);
    setChallengePoints(selected.points);
    setChallengeProgress(0);
    setChallengeCompleted(false);
  }, [modeId]);
  
  useEffect(() => {
    achievementManager.setMode(isDaily ? 'daily' : modeId);
    // Force (re)apply theme in case route/mode change caused lost CSS vars (SSR hydration or fresh navigation)
    try { themeManager.ensureThemeVariables(); } catch { /* fallback silent */ }
    
    // Generate challenge for Challenge Mode
    if (modeId === 'challenge') {
      generateChallenge();
    }
  }, [modeId, isDaily, generateChallenge]);

  // Guard: if CSS variables lost due to visibility / bfcache, re-apply on visibility change
  useEffect(() => {
    const handler = () => {
      try {
        if (document.visibilityState === 'visible') {
          themeManager.ensureThemeVariables();
        }
      } catch {}
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // Load persisted series progress (if any) when entering a series-enabled mode
  useEffect(() => {
    if (seriesLength <= 1) return;
    try {
      const raw = localStorage.getItem(SERIES_STORE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.scores)) {
          seriesScoresRef.current = parsed.scores;
          setSeriesScores(parsed.scores);
          if (typeof parsed.idx === 'number') {
            setSeriesIdx(parsed.idx);
          }
        }
      }
      // restore in-progress current game state if present
      const rawGame = localStorage.getItem(SERIES_GAME_STATE_KEY);
      if (rawGame) {
        const g = JSON.parse(rawGame);
        if (g && Array.isArray(g.dice) && typeof g.rollsLeft === 'number' && g.scores) {
          setGameState(prev => ({
            ...prev,
            dice: g.dice,
            rollsLeft: g.rollsLeft,
            scores: g.scores,
            currentRound: g.currentRound || prev.currentRound,
          }));
        }
      }
      // If a completed series is stored, load summary
      const rawCompleted = localStorage.getItem(`${SERIES_STORE_KEY}:completed`);
      if (rawCompleted) {
        try {
          const c = JSON.parse(rawCompleted);
          if (c && Array.isArray(c.scores)) {
            setSeriesCompletedData({ scores: c.scores, best: c.best, avg: c.avg });
            setSeriesSummaryOpen(true);
          }
        } catch {}
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SERIES_STORE_KEY, seriesLength]);

  // Persist panel open/closed state
  useEffect(() => {
    try { localStorage.setItem('yatzy-series-panel-open', seriesPanelOpen.toString()); } catch {}
  }, [seriesPanelOpen]);

  // CHANGE (2026-02-23): Logik för att nollställa serieläge borttagen från UI enligt krav.

  // Removed automatic scroll on round start to avoid unwanted jump for user

  // CHANGE (2026-02-23): Timern i Speed/Blitz ska starta vid första kastet, inte direkt vid ny runda.
  // Vi härleder därför "aktiv" från om spelaren redan kastat minst en gång i rundan.
  useEffect(() => {
    if (!timeLimit || gameState.isGameComplete) {
      setTimerActive(false);
      return;
    }
    setTimerActive(gameState.rollsLeft < maxRolls);
  }, [timeLimit, gameState.rollsLeft, gameState.isGameComplete, maxRolls]);

  // SpeedDial stabilisering - stäng menyn vid klick utanför
  useEffect(() => {
    if (!dialOpen) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      const speedDialElement = document.querySelector('[data-speeddial]');
      
      if (speedDialElement && !speedDialElement.contains(target)) {
        setDialOpen(false);
      }
    };
    
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDialOpen(false);
      }
    };
    
    // Lägg till event listeners med en kort delay för att undvika konflikter
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }, 50);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [dialOpen]);

  // Shake-to-roll: simple device motion detector
  useEffect(() => {
    if (!shakeToRollEnabled) return;
    let lastTime = 0;
    const threshold = 15; // simple g-force threshold
    const cooldown = 1200; // ms between allowed shakes
    const onMotion = (e: DeviceMotionEvent) => {
      const a = e.accelerationIncludingGravity;
      if (!a) return;
      const mag = Math.sqrt((a.x||0)**2 + (a.y||0)**2 + (a.z||0)**2);
      const now = Date.now();
      if (mag > threshold && now - lastTime > cooldown) {
        lastTime = now;
        if (!isRolling && gameState.rollsLeft > 0) {
          handleRollDiceRef.current?.();
        }
      }
    };
    window.addEventListener('devicemotion', onMotion, { passive: true } as any);
    return () => window.removeEventListener('devicemotion', onMotion as any);
  }, [shakeToRollEnabled, isRolling, gameState.rollsLeft]);

  // Detect special combinations for glow effects
  const detectSpecialCombination = (dice: DieType[]) => {
    const values = dice.map(d => d.value).sort();
    const counts = values.reduce((acc: any, val: number) => {
      acc[val] = (acc[val] || 0) + 1;
      return acc;
    }, {});
    
    const countValues = Object.values(counts) as number[];
    
    // Yatzy animation:
    //  - Classic: 5 lika
    //  - Maxi (6 dice): endast 6 lika ("Super Yatzy") – fem lika ska inte trigga firande
    if ((!isMaxi && countValues.includes(5)) || (isMaxi && countValues.includes(6))) {
      return 'yatzy';
    }
    
    if (countValues.includes(4)) return 'fourOfAKind';
    if (countValues.includes(3) && countValues.includes(2)) return 'fullhouse';
    if (countValues.includes(3)) return 'threeOfAKind';
    
    const uniqueValues = Array.from(new Set(values)).sort();
    if (uniqueValues.length >= 5) {
      if (uniqueValues.slice(0, 5).every((val, i) => val === i + 1) ||
          uniqueValues.slice(1, 6).every((val, i) => val === i + 2)) {
        return 'straight';
      }
    }
    return null;
  };
  // For sound/music settings UI, add state if needed

  useEffect(() => {
    // Recalculate totals whenever scores change – branch for Maxi
    if (isMaxi) {
      const { total, upper, bonus } = calculateMaxiTotals(gameState.scores as any);
      setGameState(prev => {
        if (prev.totalScore === total && prev.upperSectionScore === upper && prev.upperSectionBonus === bonus) {
          return prev;
        }
        return { ...prev, totalScore: total, upperSectionScore: upper, upperSectionBonus: bonus };
      });
    } else {
      const { total, upperSection, bonus } = calculateTotalScore(gameState.scores);
      setGameState(prev => {
        if (prev.totalScore === total && prev.upperSectionScore === upperSection && prev.upperSectionBonus === bonus) {
          return prev;
        }
        return { ...prev, totalScore: total, upperSectionScore: upperSection, upperSectionBonus: bonus };
      });
    }
  }, [gameState.scores, isMaxi]);

  useEffect(() => {
    // Game completion / achievements effect decoupled för clearer dependencies
    const isComplete = Object.values(gameState.scores).every(score => score !== null);
    if (!isComplete || gameState.isGameComplete) return;
    setGameState(prev => ({ ...prev, isGameComplete: true }));
    setShowGameOver(true);
    trackEvent('game-complete', { mode: modeId, score: gameState.totalScore });
    // Ghost mode: spara som nytt rekordspel om resultatet slår nuvarande ghost.
    if (modeId !== 'challenge') {
      try {
        maybeSaveGhost(modeId, roundTotalsRef.current, gameState.totalScore, mode?.scoring === 'reverse');
      } catch {}
    }
    // CHANGE (2026-08-14): Notisfrågan ställs här – efter första avslutade
    // spelet – i stället för som permission-prompt vid appstart. Frågan visas
    // max en gång och bara om webbläsaren inte redan beviljat/nekat.
    if (shouldPromptForReminders()) {
      markReminderPromptShown();
      setTimeout(() => {
        showConfirmationToast(t('game.reminderPrompt'), {
          onConfirm: () => {
            void ensureNotificationPermission().then(granted => {
              if (granted) {
                scheduleDailyReminders();
                showToast(t('game.reminderOn'), 'success');
              }
            });
          },
          confirmText: t('game.reminderYes'),
          cancelText: t('game.reminderNo'),
          replaceKey: 'daily-reminder-optin'
        });
      }, 1500);
    }
    // Series accounting (record score once per completed game)
    if (seriesLength > 1 && !gameCountedRef.current) {
      gameCountedRef.current = true;
      seriesScoresRef.current = [...seriesScoresRef.current, gameState.totalScore];
      setSeriesScores(seriesScoresRef.current);
      // persist partial progress
      try {
        localStorage.setItem(SERIES_STORE_KEY, JSON.stringify({ scores: seriesScoresRef.current, idx: seriesIdx }));
      } catch {}
      // If series now complete, open summary modal
      if (seriesScoresRef.current.length === seriesLength) {
        const best = Math.max(...seriesScoresRef.current);
        const avg = Math.round(seriesScoresRef.current.reduce((a,b)=>a+b,0)/seriesScoresRef.current.length);
        const data = { scores: [...seriesScoresRef.current], best, avg };
        setSeriesCompletedData(data);
        setSeriesSummaryOpen(true);
        try { localStorage.setItem(`${SERIES_STORE_KEY}:completed`, JSON.stringify(data)); } catch {}
      }
    }
    soundManager.playGameComplete();
    const gameSession = {
      playerName: 'Spelare',
      finalScore: gameState.totalScore,
      scores: Object.fromEntries(
        Object.entries(gameState.scores).map(([key, value]) => [key, value || 0])
      ) as Record<string, number>,
      rollsUsed: rollsUsedPerCategory,
      duration: Date.now() - gameStartTime,
      date: new Date().toISOString().split('T')[0],
      achievements: []
    };
    statisticsManager.recordGameSession(gameSession);
    const totalRolls = rollsUsedPerCategory.reduce((sum, rolls) => sum + rolls, 0);
    const gameData = {
      scores: gameState.scores,
      totalScore: gameState.totalScore,
      dice: gameState.dice,
      rollsUsed: 3,
      totalRolls,
      currentRound: gameState.currentRound,
      modeId: modeId || 'classic',
      gameStartTime,
    };
    
    // Uppdatera streak-data om spelet är klart
    let enhancedGameData;
    if (gameState.isGameComplete) {
      enhancedGameData = achievementManager.updateStreakData(gameData);
    } else {
      enhancedGameData = gameData;
    }
    
    const unlocked = achievementManager.checkAchievements(enhancedGameData);
    if (unlocked.length > 0) {
      setCurrentAchievement(unlocked[0]);
      soundManager.playAchievement();
    }
    try {
      const items: HighlightItem[] = [];
      const s = gameState.scores as any;
      const bestEntry = Object.entries(s).reduce<{k:string,v:number} | null>((best, [k, v]) => {
        const val = (v || 0) as number;
        if (best === null || val > best.v) return { k, v: val };
        return best;
      }, null);
      if (bestEntry && bestEntry.v > 0) {
        items.push({ icon: '🏅', title: `Bästa kategori: ${getCategoryName(bestEntry.k)}` , subtitle: `${bestEntry.v} poäng` });
      }
      if (s.yatzy && s.yatzy > 0) {
        items.push({ icon: '�', title: 'Yatzy!', subtitle: `${s.yatzy} poäng i Yatzy` });
      }
      if (s.largeStraight === 20) items.push({ icon: '🪜', title: 'Stor stege', subtitle: '20 poäng' });
      if (s.smallStraight === 15) items.push({ icon: '🪜', title: 'Liten stege', subtitle: '15 poäng' });
      items.push({ icon: '🏁', title: 'Slutpoäng', subtitle: `${gameState.totalScore} poäng` });
      setHighlights(items);
    } catch {}
  }, [gameState.scores, gameState.dice, gameState.totalScore, gameState.currentRound, gameState.isGameComplete, gameStartTime, rollsUsedPerCategory, modeId, mode?.scoring, getCategoryName, seriesLength, SERIES_STORE_KEY, seriesIdx, showConfirmationToast, showToast, t]);

  const generatePositions = React.useCallback((count:number, blockedPositions: {x:number;y:number;}[] = []) => {
    // Låt även mobila enheter få slumpmässiga positioner för bättre utseende
    if (typeof window !== 'undefined' && window.innerWidth < 420) return []; // bara mycket små skärmar använder grid
    
    // Säkerställ att vi använder fönstrets storlek som max-gränser
    const maxWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const maxHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;
    
    const rectW = Math.min(diceAreaRef.current?.clientWidth || 520, maxWidth - 40); // 40px margin från kanter
    const rectH = Math.min(diceAreaRef.current?.clientHeight || 240, maxHeight - 40);
    
    // Dynamisk tärningsstorlek baserat på skärmstorlek.
    // Klassiskt (5) och Maxi (6) använder numera samma "normal" storlek -
    // Maxi får istället ett högre tärningsområde (DiceContainer $tall) så
    // att 6 tärningar kan sprida ut sig över flera rader istället för att
    // tvingas ihop till en trång rad.
    // NOTE: Must stay in sync with `src/components/Die2D.tsx` normal-variant sizes.
    let dieSize = 104;
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 2560) {
        // Ultrawide/4K+ skärmar - mycket större tärningar
        dieSize = 148;
      } else if (window.innerWidth >= 1920) {
        // 4K skärmar - stora tärningar
        dieSize = 132;
      } else if (window.innerWidth >= 1400) {
        // Stora skärmar - medelstora tärningar
        dieSize = 112;
      }
    }

    // Die2D scales up slightly while rolling (no protruding lock badge like the old
    // 3D die had, so only a small buffer is needed to avoid clipping against edges).
    const rollingScale = 1.07;
    const overhangPx = 6;
    const effectiveDieSize = dieSize * rollingScale + overhangPx * 2;
    
    const margin = Math.max(6, effectiveDieSize * 0.06); // Margin proportionell till tärningsstorlek
    const maxAttemptsPerDie = 250;
    const positions: {x:number;y:number;}[] = [];
    const minCenterDistance = effectiveDieSize + margin;

    const isClearOfOverlaps = (candidate: {x:number;y:number;}, existing: {x:number;y:number;}[]) => {
      return !existing.some(p => Math.hypot(p.x - candidate.x, p.y - candidate.y) < minCenterDistance);
    };

    const createSafeFallbackPositions = () => {
      // Try centered rows before the final clamp fallback.
      const minX = margin + effectiveDieSize / 2;
      const maxX = Math.max(minX, rectW - margin - effectiveDieSize / 2);
      const centerY = Math.min(rectH - margin - effectiveDieSize / 2, Math.max(margin + effectiveDieSize / 2, rectH / 2));
      const safeYMin = margin + effectiveDieSize / 2;
      const safeYMax = Math.max(safeYMin, rectH - margin - effectiveDieSize / 2);
      const availableW = Math.max(0, maxX - minX);

      const maxRows = Math.max(1, Math.ceil(count / 2));
      // Rows must be at least minCenterDistance apart - two dice directly above/below
      // each other in adjacent rows are checked against the same overlap threshold as
      // any other pair, so a smaller row gap would make every multi-row attempt below
      // fail its own overlap check and silently collapse back to a single row.
      // The +2px nudges rowStep comfortably above minCenterDistance instead of
      // exactly equal to it - at exact equality, floating-point rounding can push
      // the computed row-to-row distance a hair under the threshold, making the
      // overlap check below fail spuriously every time.
      const rowStep = Math.max(minCenterDistance + 2, margin + 10);

      for (let rows = 1; rows <= maxRows; rows++) {
        const rowOffsets = Array.from({ length: rows }, (_, rowIdx) => (rowIdx - (rows - 1) / 2) * rowStep);
        const candidatePositions: {x:number;y:number;}[] = [];
        let placementFailed = false;

        for (let i = 0; i < count; i++) {
          const rowIdx = i % rows;
          const itemsInRow = Math.ceil((count - rowIdx) / rows);
          const idxInRow = Math.floor(i / rows);
          const step = itemsInRow > 1 ? Math.min(minCenterDistance, availableW / (itemsInRow - 1)) : 0;
          const startX = itemsInRow > 1 ? (minX + maxX) / 2 - step * (itemsInRow - 1) / 2 : (minX + maxX) / 2;
          const x = Math.min(maxX, Math.max(minX, startX + idxInRow * step));
          const y = Math.min(safeYMax, Math.max(safeYMin, centerY + rowOffsets[rowIdx]));
          const candidate = { x, y };

          if (!isClearOfOverlaps(candidate, [...blockedPositions, ...candidatePositions])) {
            placementFailed = true;
            break;
          }
          candidatePositions.push(candidate);
        }

        if (!placementFailed && candidatePositions.length === count) {
          return candidatePositions;
        }
      }

      // Final clamp fallback (always returns in-bounds positions).
      const step = count > 1 ? Math.min(minCenterDistance, availableW / (count - 1)) : 0;
      const startX = count > 1 ? (minX + maxX) / 2 - step * (count - 1) / 2 : (minX + maxX) / 2;
      return Array.from({ length: count }, (_, i) => ({
        x: Math.min(maxX, Math.max(minX, startX + i * step)),
        y: centerY
      }));
    };
    
    // Säkerställ att tärningarna aldrig hamnar utanför viewport
    const safeLeftMin = margin + effectiveDieSize/2;
    const safeLeftMax = Math.max(safeLeftMin + 10, rectW - margin - effectiveDieSize/2);
    const safeTopMin = margin + effectiveDieSize/2;
    const safeTopMax = Math.max(safeTopMin + 10, rectH - margin - effectiveDieSize/2);
    
    // Extra kontroll att vi har tillräckligt utrymme
    if (safeLeftMax <= safeLeftMin || safeTopMax <= safeTopMin) {
      // Inte tillräckligt utrymme för slumpmässiga positioner – använd säker fallback
      return createSafeFallbackPositions();
    }
    
    for (let i=0;i<count;i++) {
      let placed = false;
      for (let attempt=0; attempt<maxAttemptsPerDie; attempt++) {
        const x = safeLeftMin + Math.random()*(safeLeftMax - safeLeftMin);
        const y = safeTopMin + Math.random()*(safeTopMax - safeTopMin);
        const overlaps = [...blockedPositions, ...positions].some(
          p => Math.hypot(p.x - x, p.y - y) < minCenterDistance
        );
        if (!overlaps) { positions.push({x,y}); placed = true; break; }
      }
      if (!placed) {
        // fallback: always in-bounds
        return createSafeFallbackPositions();
      }
    }
    return positions;
  }, [diceCount]);
  // initial & on resize - detect mobile & landscape orientation
  useEffect(()=> {
    const handleResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      
      // Detect mobile: pointer is coarse (touch device) or small screen
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                       (typeof window !== 'undefined' && window.matchMedia('(pointer:coarse)').matches) ||
                       (w < 1024 && h < 1024); // Small screens
      // Förbättrade breakpoints: mobil alltid 2D, desktop kan vara 3D
      const narrow = isMobile || w < 640 || (w < 900 && h < 500);

      setIsNarrow(narrow);
      if (!narrow) setDicePositions(generatePositions(gameState.dice.length));
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [generatePositions, gameState.dice.length]);

  const handleRollDice = async () => {
    if (gameState.rollsLeft > 0) {
      // Nytt kast stänger ångra-fönstret för föregående kategorival.
      lastMoveRef.current = null;
      // Mark that the user is active today on first real interaction
      try { markPlayedToday(); } catch {}
      vibrateMedium();
      setDiceGlowType(null);

      // Räkna ut utfall och nya positioner REDAN NU (inte efter 500ms-fördröjningen),
      // så att tärningens snurr-animation och dess förflyttning till slutpositionen
      // startar och pågår samtidigt - annars snurrar tärningen kvar på gamla platsen
      // och "hoppar" sedan till en ny plats först när kastet är klart.
      let newDice: any[];
      if (isDaily) {
        // Lazy-init RNG with today's seed
        if (!rngRef.current) rngRef.current = mulberry32(todaySeed());
        const rng = rngRef.current;
        newDice = gameState.dice.map(d => ({
          ...d,
          value: d.isLocked ? d.value : Math.floor((rng!() * 6)) + 1
        }));
      } else {
        newDice = gameState.dice.map(d => ({
          ...d,
          value: d.isLocked ? d.value : Math.floor(Math.random() * 6) + 1
        }));
      }

      setIsRolling(true);
      setGameState(prev => ({
        ...prev,
        dice: newDice,
        rollsLeft: prev.rollsLeft - 1
      }));
      // Uppdatera bara positioner för icke-låsta tärningar
      setDicePositions(prevPositions => {
        const newPositions = [...prevPositions];
        const unlockedIndices = newDice.map((die, index) => ({ die, index }))
          .filter(({ die }) => !die.isLocked)
          .map(({ index }) => index);

        if (unlockedIndices.length > 0) {
          const lockedPositions = newDice
            .map((die, index) => ({ die, index }))
            .filter(({ die }) => die.isLocked)
            .map(({ index }) => prevPositions[index])
            .filter((pos): pos is {x:number;y:number;} => Boolean(pos));

          const tempPositions = generatePositions(unlockedIndices.length, lockedPositions);
          unlockedIndices.forEach((diceIndex, tempIndex) => {
            if (tempPositions[tempIndex]) {
              newPositions[diceIndex] = tempPositions[tempIndex];
            }
          });
        }

        return newPositions;
      });

      await soundManager.playDiceRoll();

      setTimeout(() => {
        const specialCombo = detectSpecialCombination(newDice);
        if (specialCombo) {
          setDiceGlowType(specialCombo as any);
          if (specialCombo === 'yatzy') {
            setParticleEffect({ type: 'yatzy', x: window.innerWidth / 2, y: window.innerHeight / 2 });
          }
        }

        setIsRolling(false);
        // CHANGE (2026-02-23): Ingen manuell start här – timerActive härleds från rollsLeft.
        // Persist in-progress game (only for series) after roll resolves
        if (seriesLength > 1) {
          try {
            localStorage.setItem(SERIES_GAME_STATE_KEY, JSON.stringify({
              dice: newDice,
              rollsLeft: gameState.rollsLeft - 1,
              scores: gameState.scores,
              currentRound: gameState.currentRound,
            }));
          } catch {}
        }
      }, 500);
    }
  };

  handleRollDiceRef.current = handleRollDice;

  const handleDieClick = async (dieId: number) => {
    if (gameState.rollsLeft < maxRolls) {
      setGameState(prev => {
        const wasLocked = prev.dice.find(d => d.id === dieId)?.isLocked;
        const newDice = toggleDieLock(prev.dice, dieId);
        if (wasLocked) {
          soundManager.playDiceUnlock();
        } else {
          soundManager.playDiceLock();
        }
        vibrateShort();
        return { ...prev, dice: newDice };
      });
    }
  };

  handleDieClickRef.current = handleDieClick;

  const handleSetLockedIds = useCallback((lockedIds: number[]) => {
    if (gameState.rollsLeft < maxRolls) {
      const targetSet = new Set(lockedIds);
      setGameState(prev => ({
        ...prev,
        dice: prev.dice.map(d => ({ ...d, isLocked: targetSet.has(d.id) })),
      }));
      soundManager.playDiceLock();
      vibrateShort();
    }
  }, [gameState.rollsLeft, maxRolls]);

  // CHANGE (2026-08-15): Ångra senaste kategorival – vanligaste frustrationen
  // är ett felklick på fel rad. Återställer poäng, tärningar och kast-läge
  // exakt som före valet. (Achievement-triggers backas inte – medvetet.)
  const undoLastMove = () => {
    const lastMove = lastMoveRef.current;
    if (!lastMove) return;
    lastMoveRef.current = null;
    roundTotalsRef.current = roundTotalsRef.current.slice(0, -1);
    setRollsUsedPerCategory(prev => prev.slice(0, -1));
    setJustScoredCategory(null);
    setGameState(prev => ({
      ...prev,
      scores: { ...(prev.scores as any), [lastMove.category]: null } as any,
      dice: lastMove.dice as any,
      rollsLeft: lastMove.rollsLeft,
      currentRound: lastMove.currentRound,
      isGameComplete: false,
    }));
    soundManager.playButtonClick();
  };

  const commitScore = (category: string, score: number, rollsUsedThisRound: number) => {
    // Snapshot för ångra – innan någon state muteras.
    const remainingBefore = Object.values(gameState.scores).filter(v => v === null).length;
    const allowUndo = !timeLimit && modeId !== 'challenge' && remainingBefore > 1;
    lastMoveRef.current = allowUndo
      ? { category, dice: gameState.dice, rollsLeft: gameState.rollsLeft, currentRound: gameState.currentRound }
      : null;
    // Ghost mode: kumulativ total efter denna omgång (inkl. bonus i den punkten).
    {
      const scoresAfter: any = { ...gameState.scores, [category]: score };
      const totalAfter = isMaxi
        ? calculateMaxiTotals(scoresAfter).total
        : calculateTotalScore(scoresAfter).total;
      roundTotalsRef.current = [...roundTotalsRef.current, totalAfter];
    }
    // Track rolls used for this category
    setRollsUsedPerCategory(prev => [...prev, rollsUsedThisRound]);

    // Play special sound for Yatzy
  if (category === 'yatzy' && score >= 50) {
      // Detect potential super yatzy (6 dice variant) defensively if diceCount is 6 in future modes
      const counts = new Array(7).fill(0); gameState.dice.forEach(d=> counts[d.value]++);
      const isSuper = counts.some(c=> c>=6) && score === 100;
      if(isSuper && (soundManager as any).playSuperYatzy){ (soundManager as any).playSuperYatzy(); }
      else soundManager.playYatzy();
      setParticleEffect({
        type: 'yatzy',
        x: window.innerWidth / 2,
        y: window.innerHeight / 2
      });
      vibrateAchievement();
    } else {
      soundManager.playScoreSelect();
      vibrateScore();
    }
    // Cheer toasts (simple thresholds)
    const cheer = score >= 50 ? '🔥 Fantastiskt kast!' : score >= 40 ? '👏 Grym poäng!' : score >= 30 ? '💪 Snyggt!' : null;
    if (cheer) {
          // Clear existing timeouts
      if (toastTimeouts.current.leave) clearTimeout(toastTimeouts.current.leave);
      if (toastTimeouts.current.remove) clearTimeout(toastTimeouts.current.remove);
      
      const currentTimeouts = toastTimeouts.current; // Capture ref for closure
      const id = ++toastCounter.current;
      setToast({ id, msg: cheer });
      currentTimeouts.leave = setTimeout(()=> setToast(t => t && t.id===id ? { ...t, leaving:true } : t), 2500);
      currentTimeouts.remove = setTimeout(()=> setToast(t => t && t.id===id ? null : t), 2900);
    }
  setJustScoredCategory(category as string);
  setTimeout(()=> setJustScoredCategory(null), 1700);
    setGameState(prev => {
      const newScores: any = { ...prev.scores, [category]: score };
  // (Round event logging removed)
      // Check for achievements on score select
      const totalRolls = rollsUsedPerCategory.reduce((sum, rolls) => sum + rolls, 0) + rollsUsedThisRound;
      const gameData = {
        scores: newScores,
        totalScore: prev.totalScore,
        dice: prev.dice,
        rollsUsed: rollsUsedThisRound,
        totalRolls,
        currentRound: prev.currentRound,
        modeId: modeId || 'classic',
        gameStartTime,
      };
      const unlocked = achievementManager.checkAchievements(gameData);
      if (unlocked.length > 0) {
        setCurrentAchievement(unlocked[0]);
        soundManager.playAchievement();
        vibrateAchievement();
        setParticleEffect({
          type: 'achievement',
          x: window.innerWidth / 2,
          y: window.innerHeight / 3
        });
      }
      return {
        ...prev,
        scores: newScores,
        dice: resetDice(diceCount as 5 | 6),
        rollsLeft: maxRolls,
        currentRound: prev.currentRound + 1,
      };
    });
    // Remove persisted in-progress state after committing a score (round ends)
    try { localStorage.removeItem(SERIES_GAME_STATE_KEY); } catch {}
    if (timeLimit) setTimerActive(false);
    if (lastMoveRef.current) {
      const categoryLabel = getCategoryName(category);
      showToast(t('game.undoToast', { category: categoryLabel, score }), 'info', {
        duration: 6000,
        action: { label: t('game.undo'), onClick: undoLastMove, variant: 'secondary' },
      });
    }
  };

  const handleScoreSelect = (category: any) => {
    if (isRolling) return;
    if ((gameState.scores as any)[category] !== null) return;
    if (gameState.rollsLeft === maxRolls && !timeLimit) return; // require at least one roll in non-timed modes

    const baseScore = isMaxi ? calculateMaxiScore(gameState.dice as any, category) : calculatePossibleScore(gameState.dice, category);
    // CHANGE (2026-02-23): Blitz ska inte ge dubbla poäng.
    const score = (mode?.scoring === 'multiplied') ? (baseScore * scoreMultiplier) : baseScore;
    const rollsUsedThisRound = Math.max(1, maxRolls - gameState.rollsLeft + 1);
    
    // Update challenge progress for Challenge Mode
    if (modeId === 'challenge' && !challengeCompleted) {
      let updatedProgress = challengeProgress;
      let completed = false;
      
      // Check challenge type and update progress
      switch (challengeType) {
        case 'upperSection':
          // Check if this score contributes to upper section
          if (['ones', 'twos', 'threes', 'fours', 'fives', 'sixes'].includes(category)) {
            updatedProgress += score;
            if (updatedProgress >= challengeTarget) completed = true;
          }
          break;
        case category: // Direct category match (fullHouse, yatzy, etc.)
          if (score >= challengeTarget) completed = true;
          updatedProgress = score;
          break;
        case 'fewRolls':
          if (rollsUsedThisRound <= challengeTarget) {
            completed = true;
            updatedProgress = challengeTarget;
          }
          break;
        case 'bonus':
          // Calculate new upper section score with this category
          const newScores = { ...gameState.scores, [category]: score };
          const { upperSection } = calculateTotalScore(newScores);
          updatedProgress = upperSection;
          if (upperSection >= challengeTarget) completed = true;
          break;
      }
      
      setChallengeProgress(updatedProgress);
      
      if (completed && !challengeCompleted) {
        setChallengeCompleted(true);
        // Show bonus points notification
        showToast(`Utmaning avklarad! +${challengePoints} bonuspoäng!`, 'success');
        try { vibrateAchievement(); } catch {}
        // Add bonus points
        setGameState(prev => ({
          ...prev,
          totalScore: prev.totalScore + challengePoints
        }));
      }
    }
    
    commitScore(category, score, rollsUsedThisRound);
  };

  // Auto-assign on time up for Speed/Blitz modes
  const handleTimeUp = () => {
    // Timeout rule for timed modes: strike (0 points) in an empty category
    const emptyCategories = Object.keys(gameState.scores).filter(k => (gameState.scores as any)[k] === null) as (keyof YatzyScores)[];
    if (emptyCategories.length === 0) return;
    // Deterministic choice keeps behavior predictable and test-friendly
    const selected = emptyCategories[0];
    // On timeout, count as using all rolls
    commitScore(selected, 0, maxRolls);
  };

  const handleNewGame = () => {
    // If game is already complete, just start new game without confirmation
    if (gameState.isGameComplete) {
      setShowGameOver(false);
      resetGameState();
      return;
    }

    // Check if game is in progress
    const isGameInProgress = () => {
      // Game is in progress if any category has been scored
      const hasAnyScores = Object.values(gameState.scores).some(score => score !== null);
      return hasAnyScores;
    };

    // If we are in a series and current game is complete, progress without prompt
    if (seriesLength > 1 && gameState.isGameComplete) {
      // If more games remain in the series, advance to next; else reset series
      if (seriesIdx < seriesLength - 1) {
        setSeriesIdx(i => i + 1);
        resetGameState();
        gameCountedRef.current = false;
        setShowGameOver(false);
        // Informative toast
        const id = ++toastCounter.current;
        setToast({ id, msg: `Omgång ${seriesIdx + 2} av ${seriesLength}` });
        toastTimeouts.current.leave = setTimeout(()=> setToast(t => t && t.id===id ? { ...t, leaving:true } : t), 1800);
        toastTimeouts.current.remove = setTimeout(()=> setToast(t => t && t.id===id ? null : t), 2150);
        return;
      } else {
        // Series done – open summary modal (scores already recorded on completion effect)
        if (seriesScoresRef.current.length === seriesLength) {
          const best = Math.max(...seriesScoresRef.current);
          const avg = Math.round(seriesScoresRef.current.reduce((a,b)=>a+b,0)/seriesScoresRef.current.length);
          const data = { scores: [...seriesScoresRef.current], best, avg };
          setSeriesCompletedData(data);
          setSeriesSummaryOpen(true);
          try { localStorage.setItem(`${SERIES_STORE_KEY}:completed`, JSON.stringify(data)); } catch {}
        }
        return; // keep current state until user chooses action in modal
      }
    }

    if (isGameInProgress()) {
      // Show confirmation toast if game is in progress
      showConfirmationToast(t('game.newGameConfirm'), {
        onConfirm: () => {
          resetGameState();
        },
        confirmText: t('game.newGameYes'),
        cancelText: t('game.newGameNo')
      });
    } else {
      // Start new game immediately if no game in progress
      resetGameState();
    }
  };

  function resetGameState() {
  try { markPlayedToday(); } catch {}
    lastMoveRef.current = null;
    roundTotalsRef.current = [];
    if (canPersistGame) clearSavedGame();
    const classic: YatzyScores = { ones:null, twos:null, threes:null, fours:null, fives:null, sixes:null, onePair:null, twoPairs:null, threeOfAKind:null, fourOfAKind:null, smallStraight:null, largeStraight:null, fullHouse:null, chance:null, yatzy:null };
    const maxi: any = { ones:null, twos:null, threes:null, fours:null, fives:null, sixes:null, onePair:null, twoPairs:null, threeOfAKind:null, fourOfAKind:null, fiveOfAKind:null, smallStraight:null, largeStraight:null, fullStraight:null, hus:null, torn:null, chance:null, yatzy:null };

    setGameState(prev => ({
      dice: resetDice((diceCount as 5 | 6) || 5) as any,
      rollsLeft: maxRolls,
      scores: (diceCount === 6 ? maxi : classic) as any,
      currentRound: 1,
      isGameComplete: false,
      totalScore: 0,
      upperSectionScore: 0,
      upperSectionBonus: 0,
    }));
    setShowGameOver(false);
    setGameStartTime(Date.now());
    setRollsUsedPerCategory([]);
    
    // Reset challenge for Challenge Mode
    if (modeId === 'challenge') {
      setChallengeCompleted(false);
      setChallengeProgress(0);
      generateChallenge();
    }
    
    // keep series state; only reset accounting per-game flag
    gameCountedRef.current = false;
    soundManager.playGameStart();
    try { localStorage.removeItem(SERIES_GAME_STATE_KEY); } catch {}
  }

  resetGameStateRef.current = resetGameState;

  // Speed dial order persistence (MRU). Stored globally per device.
  const ORDER_KEY = 'yatzy-speed-dial-order';
  const dialOrder = React.useMemo(() => {
    try { const raw = localStorage.getItem(ORDER_KEY); if (raw) { const arr = JSON.parse(raw); if (Array.isArray(arr)) return arr; } } catch {}
    return ['ach','hs','stats','facts','chlog','ai'];
  }, []);
  
  // Speed dial actions
  const dialActions: {[key: string]: {label: string, tip: string, icon: string}} = {
    ach: { label: 'Prestationer', tip: 'Visa prestationer', icon: '🏆' },
    hs: { label: 'Rekord', tip: 'Visa rekordlista', icon: '📊' },
    stats: { label: 'Statistik', tip: 'Visa spelstatistik', icon: '📈' },
    facts: { label: 'Fakta', tip: 'Visa roliga fakta', icon: '🎯' },
    chlog: { label: 'Ändringar', tip: 'Visa ändringslogg', icon: '📝' },
    ai: { label: 'AI-Assistent', tip: aiAssistantEnabled ? 'Stäng av AI-hjälp' : 'Slå på AI-hjälp', icon: aiAssistantEnabled ? '🤖✨' : '🤖' }
  };
  
  const handleDialAction = (key: string) => {
    switch (key) {
      case 'ach': setShowAchievements(true); break;
      case 'hs': setShowHighScores(true); break;
      case 'stats': setShowStatistics(true); break;
      case 'facts': setShowFunFacts(true); break;
      case 'chlog': setChangelogOpen(true); break;
      case 'ai': 
        const newState = !aiAssistantEnabled;
        setAiAssistantEnabled(newState);
        try {
          localStorage.setItem('yatzy-ai-assistant-enabled', newState.toString());
        } catch {}
        break;
    }
    setDialOpen(false);
  };
  
  const compact = getCompactMode();
  return (
    <GameContainer>
      <ToastStack>{toast && <ToastMsg $leaving={toast.leaving}>{toast.msg}</ToastMsg>}</ToastStack>
      
      {/* Desktop persistent controls - DOLD */}


      {/* Alltid synliga knappar i alla hörn - standardiserade positioner */}
      <FabContainer>
        {/* Hemknapp: uppe vänster */}
        <HomeButton $pos="tl" aria-label="Hem" data-tip="Hem" onClick={() => {
          // CHANGE (2026-08-15): SPA-navigering via onExit (ingen sidomladdning).
          // Sparbara lägen förlorar inget – de kan återupptas från startskärmen.
          showConfirmationToast(
            canPersistGame
              ? t('game.homeConfirm.resumable')
              : t('game.homeConfirm.lost'),
            {
              onConfirm: () => {
                if (onExit) onExit();
                else window.location.href = window.location.origin + window.location.pathname;
              },
              confirmText: t('game.homeYes'),
              cancelText: t('game.homeNo')
            }
          );
        }}>🏠</HomeButton>
        
        {/* Hamburger: uppe höger */}
        <Fab $pos="tr" aria-label="Meny" data-tip="Meny" data-tip-pos="left" onClick={() => setMenuOpen(true)}>☰</Fab>
        
        {/* ?-knapp: nere vänster */}
        <Fab $pos="bl" aria-label="Hjälp" data-tip="Hjälp" onClick={() => setShowHelp(v => !v)}>?</Fab>
        
        {/* +-knapp: nere höger */}
        <SpeedDialWrapper data-speeddial>
          <DialActions $open={dialOpen} aria-hidden={!dialOpen} role="menu">
            {dialOrder.filter(k => !compact || (k !== 'ach')).map(k => {
              const a = dialActions[k]; 
              if (!a) return null;
              return (
                <DialBtn 
                  key={k} 
                  aria-label={a.label} 
                  data-tip={a.tip} 
                  role="menuitem" 
                  onClick={()=>handleDialAction(k)}
                  $isAiActive={k === 'ai' && aiAssistantEnabled}
                >
                  {a.icon}
                </DialBtn>
              );
            })}
          </DialActions>
          <MainDial aria-label="Snabbmeny" aria-haspopup="true" aria-expanded={dialOpen} onClick={()=>setDialOpen(o=>!o)}>{dialOpen? '×':'+'}</MainDial>
        </SpeedDialWrapper>
      </FabContainer>
      
      <Header>
        <Title>🎲 Yatzy{diceCount === 6 ? t('game.title.maxi') : ''}{isDaily ? t('game.title.daily') : ''}</Title>
        <Subtitle>
          {t('game.subtitle')}
          {seriesLength > 1 && (
            <span style={{ display:'block', marginTop:6, opacity:.95 }}>
              🥇 Serie: omgång {Math.min(seriesIdx + (gameState.isGameComplete?1:1), seriesLength)} av {seriesLength}
              {seriesScores.length > 0 && (
                <>
                  {' '}• Bäst: {Math.max(...seriesScores)}p{seriesScores.length>1?` • Snitt: ${Math.round(seriesScores.reduce((a,b)=>a+b,0)/seriesScores.length)}p`:''}
                </>
              )}
            </span>
          )}
          {isDaily && dailyCountdown && (
            <span style={{ display: 'block', marginTop: 6 }}>
              {t('game.nextDaily')} {dailyCountdown}
            </span>
          )}
        </Subtitle>
      </Header>

      {seriesLength > 1 && (
        <SeriesPanel aria-live="polite">
          <SeriesHeader>
            <h3>🥇 Serie: {seriesScores.length}/{seriesLength} klar{seriesLength>1?'e':''}</h3>
            <SeriesToggleBtn onClick={()=>setSeriesPanelOpen(o=>!o)}>{seriesPanelOpen? 'Dölj' : 'Visa'}</SeriesToggleBtn>
            {/* CHANGE (2026-02-23): "Nollställ" borttagen enligt krav. */}
          </SeriesHeader>
          {seriesPanelOpen && (
            <div>
              <SeriesList>
                {Array.from({length: seriesLength}).map((_,i)=> {
                  const doneScore = seriesScores[i];
                  const active = i === seriesScores.length && !gameState.isGameComplete;
                  const best = seriesScores.length>0 ? Math.max(...seriesScores) : null;
                  const isBest = doneScore!=null && best!=null && doneScore===best && seriesScores.filter(s=>s===best).length===1;
                  return (
                    <SeriesItem key={i} $active={active} $done={doneScore!=null}>
                      <span style={{fontSize:12,opacity:.85}}>Runda {i+1}</span>
                      <strong style={{fontSize:16,lineHeight:1}}>{doneScore!=null? `${doneScore}p` : active? 'Pågår' : '—'}</strong>
                      {isBest && <span style={{position:'absolute',top:4,right:6,fontSize:14}} role="img" aria-label="Bästa runda" title="Bästa runda">⭐</span>}
                    </SeriesItem>
                  );
                })}
              </SeriesList>
              <SeriesStats>
                <span>🏅 Bäst: {seriesScores.length>0? Math.max(...seriesScores): '—'}</span>
                <span>📈 Snitt: {seriesScores.length>0? Math.round(seriesScores.reduce((a,b)=>a+b,0)/seriesScores.length): '—'}</span>
                <span>🎯 Kvar: {Math.max(0, seriesLength - seriesScores.length - (gameState.isGameComplete?0:1))}</span>
                {seriesScores.length===seriesLength && <span>✅ Klar</span>}
              </SeriesStats>
              <SeriesProgressOuter aria-label="Series progress">
                <SeriesProgressInner $pct={Math.min(100, ((seriesScores.length + (gameState.isGameComplete?1:0)) / seriesLength) * 100)} />
              </SeriesProgressOuter>
            </div>
          )}
        </SeriesPanel>
      )}

      <GameBoard>
        <DiceSection>
          {timeLimit && (
            <div style={{ alignSelf: 'stretch', marginBottom: 10, display: 'flex', justifyContent: 'center' }}>
              <GameTimer timeLimit={timeLimit} isActive={timerActive} onTimeUp={handleTimeUp} />
            </div>
          )}
          
          {modeId === 'challenge' && (
            <div style={{ alignSelf: 'stretch', marginBottom: 10 }}>
              <ChallengeIndicator 
                active={!challengeCompleted} 
                mode={modeId}
                goal={challengeGoal}
                points={challengePoints}
                progress={challengeProgress}
                maxProgress={challengeTarget}
              />
            </div>
          )}
          <GameInfo>
            <RollInfo>
              {(() => {
                const used = maxRolls - gameState.rollsLeft; // 0 before any roll
                if (used === 0) return t('game.firstRoll');
                if (used >= maxRolls) return t('game.pickCategory');  // All rolls used
                if (used === maxRolls - 1) return t('game.lastRoll');
                return t('game.nthRoll', { n: used + 1 });
              })()}
            </RollInfo>
            <RoundInfo>
              {/* CHANGE (2026-08-15): Härled antal omgångar från kategorilistan
                  (Maxi visade tidigare hårdkodat "av 19" – det är 18 kategorier). */}
              {t('game.round', { round: gameState.currentRound, total: isMaxi ? maxiScoreCategories.length : scoreCategories.length })}
            </RoundInfo>
            {(() => {
              // Ghost mode: jämför mot rekordspelet efter samma antal omgångar.
              if (!ghost || gameState.isGameComplete) return null;
              const roundsPlayed = Object.values(gameState.scores).filter(v => v !== null).length;
              if (roundsPlayed === 0) return null;
              const ghostTotal = ghost.totals[roundsPlayed - 1];
              if (ghostTotal === undefined) return null;
              const delta = gameState.totalScore - ghostTotal;
              const lowerIsBetter = mode?.scoring === 'reverse';
              const ahead = lowerIsBetter ? delta <= 0 : delta >= 0;
              const deltaText = `${delta >= 0 ? '+' : ''}${delta}`;
              return (
                <GhostCompareRow $ahead={ahead} aria-live="polite">
                  <span role="img" aria-label="Rekordjämförelse">👻</span>
                  {t('game.ghost', { delta: deltaText, final: ghost.finalScore })}
                  <span aria-hidden="true">{ahead ? '▲' : '▼'}</span>
                </GhostCompareRow>
              );
            })()}
          </GameInfo>

          <GlowEffects type={diceGlowType || 'dice'}>
            <DiceBoard3D
              dice={gameState.dice}
              isRolling={isRolling}
              canInteract={gameState.rollsLeft < maxRolls && !isRolling}
              onToggleDie={handleDieClick}
              onSetLockedIds={handleSetLockedIds}
              onPointerDownDie={startLongPress}
              onPointerUpDie={cancelLongPress}
              onPointerLeaveDie={cancelLongPress}
            />
          </GlowEffects>
          {false && (isNarrow ? (
            <MobileDiceContainer className={diceCount === 6 ? 'six-dice' : 'five-dice'}>
              {gameState.dice.map(die => (
                <div
                  key={die.id}
                  onPointerDown={() => startLongPress(die.id)}
                  onPointerUp={() => cancelLongPress(die.id)}
                  onPointerLeave={() => cancelLongPress(die.id)}
                  onContextMenu={(e) => { e.preventDefault(); /* prevent default menu on long-press */ }}
                >
                  <Die2D
                    value={die.value}
                    isLocked={die.isLocked}
                    onClick={() => handleDieClick(die.id)}
                    isRolling={isRolling && !die.isLocked}
                    variant='normal'
                    index={die.id}
                  />
                </div>
              ))}
            </MobileDiceContainer>
          ) : (
            <DiceContainer ref={diceAreaRef} $tall={diceCount === 6}>
              <GlowEffects type={diceGlowType || 'dice'}>
                {gameState.dice.map((die, idx) => {
                  const pos = dicePositions[idx];
                  return (
                    <PositionedDie
                      key={die.id}
                      $x={pos?.x || 40 + idx*90}
                      $y={pos?.y || 120}
                      $rolling={isRolling && !die.isLocked}
                      onPointerDown={() => startLongPress(die.id)}
                      onPointerUp={() => cancelLongPress(die.id)}
                      onPointerLeave={() => cancelLongPress(die.id)}
                      onContextMenu={(e) => { e.preventDefault(); }}
                    >
                      <Die2D
                        value={die.value}
                        isLocked={die.isLocked}
                        onClick={() => handleDieClick(die.id)}
                        isRolling={isRolling && !die.isLocked}
                        variant="normal"
                        index={idx}
                      />
                    </PositionedDie>
                  );
                })}
              </GlowEffects>
            </DiceContainer>
          ))}

          <ButtonContainer>
            <Button
              onClick={handleRollDice}
              disabled={gameState.rollsLeft === 0 || isRolling}
            >
              {isRolling ? t('game.rollBtn.rolling') : `🎲 ${getRollButtonText(gameState.rollsLeft)}`}
            </Button>
            
            <Button
              $variant="secondary"
              onClick={() => {
                soundManager.playButtonClick();
                setShowHighScores(true);
              }}
            >
              {t('game.highscore')}
            </Button>
            
            <Button
              $variant="secondary"
              onClick={() => {
                soundManager.playButtonClick();
                setShowStatistics(true);
              }}
            >
              {t('game.statistics')}
            </Button>
            
            <Button
              $variant="danger"
              onClick={() => {
                soundManager.playButtonClick();
                handleNewGame();
              }}
            >
              {t('game.newGame')}
            </Button>
          </ButtonContainer>

          {/** Instruktionsrutan borttagen – info finns nu under ?-knappen */}
        </DiceSection>

  <SidebarSection>
      {diceCount === 6 ? (
              <MaxiScoreCard
              ref={scoreCardRef}
              scores={gameState.scores as any}
              dice={gameState.dice}
        onScoreSelect={handleScoreSelect as any}
              rollsLeft={gameState.rollsLeft}
              upperSectionScore={gameState.upperSectionScore}
              upperSectionBonus={gameState.upperSectionBonus}
              totalScore={gameState.totalScore}
                highlightCategory={justScoredCategory}
                forcedOrder={mode?.id==='forced-maxi' ? (maxiScoreCategories.map(c=>c.key) as any) : undefined}
                keyboardNav
                maxRolls={maxRolls}
            />
          ) : (
            <ScoreCard
              ref={scoreCardRef}
              scores={gameState.scores}
              dice={gameState.dice}
              onScoreSelect={handleScoreSelect}
              rollsLeft={gameState.rollsLeft}
              upperSectionScore={gameState.upperSectionScore}
              upperSectionBonus={gameState.upperSectionBonus}
              totalScore={gameState.totalScore}
              highlightCategory={justScoredCategory}
              forcedOrder={
                mode?.id === 'forced-classic'
                  ? standardForcedOrder
                  : mode?.id === 'reverse'
                  ? reverseForcedOrder
                  : undefined
              }
              keyboardNav
              scoringType={mode?.scoring || 'normal'}
              scoreMultiplier={scoreMultiplier}
              maxRolls={maxRolls}
            />
          )}
  </SidebarSection>
      </GameBoard>

      <HighScoreList
        isVisible={showHighScores}
        onClose={() => setShowHighScores(false)}
        modeId={isDaily ? dailyDateKey : modeId}
      />

      <StatisticsModal
        isVisible={showStatistics}
        onClose={() => setShowStatistics(false)}
      />

      <GameOverModal
        isVisible={showGameOver}
        totalScore={gameState.totalScore}
        onNewGame={handleNewGame}
        onViewHighScores={() => {
          setShowGameOver(false);
          setShowHighScores(true);
        }}
        modeId={isDaily ? dailyDateKey : modeId}
        highlights={highlights}
      />

      <AchievementNotification
        achievement={currentAchievement}
        onClose={() => setCurrentAchievement(null)}
      />

      {/* Mobile overlays */}
      <MobileMenu
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        activeGame={!gameState.isGameComplete && Object.values(gameState.scores).some(v=>v!==null)}
        aiEnabled={aiAssistantEnabled}
        onToggleAi={() => {
          const v = !aiAssistantEnabled; setAiAssistantEnabled(v); try { localStorage.setItem('yatzy-ai-assistant-enabled', v.toString()); } catch {}
        }}
        effectsEnabled={effectsEnabled}
        onToggleEffects={() => { const v = !effectsEnabled; setEffectsEnabled(v); try { localStorage.setItem('yatzy-effects-enabled', v.toString()); } catch {} }}
        shakeToRollEnabled={shakeToRollEnabled}
        onToggleShakeToRoll={() => { const v = !shakeToRollEnabled; setShakeToRollEnabled(v); try { localStorage.setItem('yatzy-shake-to-roll', v.toString()); } catch {} }}
        onHome={() => {
          // SPA-navigering via onExit; fallback till reload om prop saknas.
          if (onExit) onExit();
          else window.location.href = window.location.origin + window.location.pathname;
        }}
      />
      <AchievementsModal isVisible={showAchievements} onClose={() => setShowAchievements(false)} modeId={modeId} />
      {showHelp && (
        <div style={{ position:'fixed', bottom: 0, left:0, right:0, background:'var(--theme-card-background,#fff)', padding:'16px 18px 26px', borderTop:'3px solid var(--theme-primary,#4caf50)', zIndex:1200, boxShadow:'0 -4px 24px rgba(0,0,0,0.25)', maxHeight:'70vh', overflowY:'auto' }}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
            <strong>Hur spelar du? Tips & Tricks</strong>
            <button onClick={()=>setShowHelp(false)} style={{background:'transparent',border:'none',fontSize:26,cursor:'pointer'}}>×</button>
          </div>
          <div style={{fontSize:14,lineHeight:1.5}}>
            <ol style={{margin:'0 0 12px 20px',padding:0}}>
              <li>Kasta tärningarna.</li>
              <li>Lås de du vill behålla (tryck på en tärning).</li>
              <li>Du har upp till {maxRolls} kast per omgång.</li>
              <li>Välj sedan en ledig kategori för poäng.</li>
            </ol>
            <div style={{marginBottom:12,fontWeight:600}}>Bonus: 63+ i övre sektionen ger 50 extra poäng.</div>
            <div style={{display:'grid',gap:10}}>
              <div><strong>🎯 Prioritera tidigt:</strong> Få höga fyror, femmor och sexor tidigt för att säkra bonusen.</div>
              <div><strong>🧮 Flexibel strategi:</strong> Har du många lika? Spara mot triss/kåk/fyra lika beroende på kast 2.</div>
              <div><strong>🪜 Stegar:</strong> Saknas en mittensiffra? Kasta om allt utom en sekvens för chans på liten/stor stege.</div>
              <div><strong>🔥 Spara Yatzy sent:</strong> Håll Yatzy öppen länge för maximal flexibilitet, men offra inte bonusjakten.</div>
              <div><strong>♻️ Nollning:</strong> Om en kategori blir svår: nolla hellre en låg värde-kategori (t.ex. ett par) än chans/femmor/sexor.</div>
              <div><strong>🎵 Ljud:</strong> Du kan stänga av ljudeffekter men behålla musik (övre vänstra hörnet eller meny på mobil).</div>
              <div><strong>⏱️ Tidslägen:</strong> I snabbare lägen auto-väljs bästa kategori när tiden tar slut – tänk ett kast före.</div>
              <div><strong>🏆 Highscore:</strong> Stjärna i poängraden = extra bra resultat för den kategorin!</div>
              <div><strong>🤖 AI-Assistent:</strong> Aktivera via snabbmenyn (+-knappen) för strategiska råd och optimala drag!</div>
              <div><strong>🌟 Bonusindikator:</strong> Glad gubbe betyder att bonusen är säkrad, ledsen = du behöver fler poäng i toppen.</div>
            </div>
          </div>
        </div>
      )}

  <ChangelogModal open={changelogOpen} onClose={()=>setChangelogOpen(false)} />
  <FunFactsModal open={showFunFacts} onClose={()=>setShowFunFacts(false)} />
  
  {/* AI-Assistent - endast när aktiverad */}
  {aiAssistantEnabled && (
    <StrategyHelper
      dice={gameState.dice}
      scores={gameState.scores}
      rollsLeft={gameState.rollsLeft}
      upperSectionScore={gameState.upperSectionScore}
      onSelectCategory={handleScoreSelect}
      onToggleDieLock={(index) => {
        const dieId = gameState.dice[index]?.id;
        if (dieId && gameState.rollsLeft < maxRolls) {
          handleDieClick(dieId);
        }
      }}
    />
  )}
  
  {effectsEnabled && (
      <ParticleEffects
        trigger={particleEffect}
        onComplete={() => setParticleEffect({ type: null })}
      />
  )}
  {seriesSummaryOpen && seriesCompletedData && (
    <SeriesModalBackdrop role="dialog" aria-modal="true" aria-label="Seriesammanfattning">
      <SeriesModalCard>
        <CloseSeriesBtn aria-label="Stäng" onClick={()=> setSeriesSummaryOpen(false)}>×</CloseSeriesBtn>
        <h2 style={{margin:'0 0 4px',fontSize:24,display:'flex',alignItems:'center',gap:8}}>🏁 Serie klar!</h2>
        <div style={{opacity:.85,fontSize:14,marginBottom:12}}>Sammanfattning av {seriesCompletedData.scores.length} rundor</div>
        <SeriesRoundsList>
          {seriesCompletedData.scores.map((s, i) => {
            const best = seriesCompletedData.best;
            const isBest = s === best && seriesCompletedData.scores.filter(v=>v===best).length===1;
            return (
              <li key={i} className={isBest ? 'best':''}>
                <span style={{display:'flex',alignItems:'center',gap:8}}>
                  <strong>Runda {i+1}</strong>
                </span>
                <span style={{fontWeight:700}}>{s}p {isBest && <span role="img" aria-label="Bäst" title="Bästa runda" style={{marginLeft:6}}>⭐</span>}</span>
              </li>
            );
          })}
        </SeriesRoundsList>
        <div style={{display:'flex',flexWrap:'wrap',gap:14,marginBottom:22,fontSize:14}}>
          <span>🏅 Bäst: <strong>{seriesCompletedData.best}p</strong></span>
          <span>📈 Snitt: <strong>{seriesCompletedData.avg}p</strong></span>
        </div>
        <div style={{display:'flex',gap:14,flexWrap:'wrap'}}>
          <SeriesPrimaryBtn onClick={() => {
            // start new series: clear stored data & reset series state
            setSeriesIdx(0);
            seriesScoresRef.current = [];
            setSeriesScores([]);
            gameCountedRef.current = false;
            setSeriesCompletedData(null);
            setSeriesSummaryOpen(false);
            try { localStorage.removeItem(`${SERIES_STORE_KEY}:completed`); localStorage.removeItem(SERIES_GAME_STATE_KEY); localStorage.removeItem(SERIES_STORE_KEY); } catch {}
            resetGameState();
          }}>🔄 Ny serie</SeriesPrimaryBtn>
          <SeriesSecondaryBtn onClick={() => {
            setSeriesSummaryOpen(false);
          }}>Stäng</SeriesSecondaryBtn>
        </div>
      </SeriesModalCard>
    </SeriesModalBackdrop>
  )}
    </GameContainer>
  );
};

export default YatzyGame;

